import { Helmet } from 'react-helmet'
import { Link } from 'react-router'

import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { TableSkeletonRows } from '@/components/ui/table-skeleton'
import { GeicoQaRunStatus, useGeicoQaFixtures, useStartGeicoQaRuns } from '@/models/geicoQa'
import { GeicoQaFixtureRow } from './GeicoQaFixtureRow'

export function GeicoQa() {
  const { data: fixtures, isPending, error } = useGeicoQaFixtures()
  const { mutate: startRuns, isPending: isStarting, variables, data: started } = useStartGeicoQaRuns()

  const running = fixtures?.filter((fixture) => fixture.latestRun?.status === GeicoQaRunStatus.Pending) ?? []
  const failing = fixtures?.filter((fixture) => fixture.latestRun?.passed === false) ?? []

  // Run all names no slugs, so a row would otherwise stay clickable through it.
  const isStartingSlug = (slug: string) =>
    isStarting && (variables.runAll || variables.slugs?.includes(slug) === true)

  return (
    <>
      <Helmet>
        <title>GEICO regression</title>
      </Helmet>
      <div className="flex flex-col gap-6 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">GEICO regression</h1>
            <p className="text-muted-foreground text-sm">
              Re-quotes committed customer fixtures through GEICO. A quote or a decline both pass — only a run
              that never reached a GEICO verdict fails. No org, application or fleet is created — the fixture
              is imported only long enough to build the payload.{' '}
              <Link className="underline" to="/insure/internal/qa">
                Back to QA
              </Link>
            </p>
          </div>
          <Button
            disabled={!fixtures?.length}
            loading={isStarting && variables.runAll ? 'Starting…' : undefined}
            onClick={() => startRuns({ runAll: true })}
          >
            Run all
          </Button>
        </div>

        <Banner variant="warning">
          Each run is a real GEICO portal session on the shared agent account — roughly 20 minutes, serialized
          by Supergood — and leaves a real quote in their dashboard. Results arrive on Supergood's callback,
          so locally this needs a tunnel to <code>INSURE_APP_ORIGIN</code>.
        </Banner>

        {error && <Banner variant="error">Could not load the suite: {(error as Error).message}</Banner>}

        {started && started.skipped.length > 0 && (
          <Banner variant="info">Already running, so not started again: {started.skipped.join(', ')}.</Banner>
        )}

        {fixtures && fixtures.length > 0 && (
          <div className="text-muted-foreground text-sm">
            {fixtures.length} fixture{fixtures.length === 1 ? '' : 's'} · {failing.length} failing ·{' '}
            {running.length} running
          </div>
        )}

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[26rem] min-w-[18rem]">Fixture</TableHead>
                  <TableHead className="min-w-[13rem]">Result</TableHead>
                  <TableHead>Premium</TableHead>
                  <TableHead>Last run</TableHead>
                  <TableHead>Links</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {isPending && <TableSkeletonRows columns={6} />}
                {fixtures?.length === 0 && (
                  <TableRow>
                    <TableCell className="text-muted-foreground p-6 text-center" colSpan={6}>
                      No fixtures yet. Add an export and a manifest entry under{' '}
                      <code>backend/qa_fixtures/geico_supergood/</code> — see the README there.
                    </TableCell>
                  </TableRow>
                )}
                {fixtures?.map((fixture) => (
                  <GeicoQaFixtureRow
                    fixture={fixture}
                    isStarting={isStartingSlug(fixture.slug)}
                    key={fixture.slug}
                    onRun={(slug) => startRuns({ slugs: [slug] })}
                  />
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </>
  )
}
